import type { Me, Session } from '@cribbage/protocol';

export type AnalyticsEvent =
  | 'client_opened'
  | 'signup_started'
  | 'account_created'
  | 'signed_in'
  | 'oauth_completed'
  | 'page_view';

export interface AnalyticsPayload {
  readonly event_id: string;
  readonly name: AnalyticsEvent;
  readonly page: string;
  readonly landing_page: string;
  readonly referrer: string;
}

export type AnalyticsPermission = (playerId: string) => boolean;

/** Optional attribution. The regional privacy layer must explicitly allow it. */
export class ClientAnalytics {
  private allowed: AnalyticsPermission = () => false;
  private player: Me | null = null;
  private token: string | null = null;
  private openedFor: string | null = null;
  private firstPlayer: string | null = null;

  constructor(
    private readonly send: (payload: AnalyticsPayload, token: string) => Promise<void>,
    private readonly context: () => Omit<AnalyticsPayload, 'event_id' | 'name'> = browserContext,
  ) {}

  setPermission(permission: AnalyticsPermission): void {
    this.allowed = permission;
    this.openedFor = null;
    this.open();
  }

  identify(user: Me, token: string): void {
    if (!user?.id || typeof token !== 'string' || token === '') return;
    this.firstPlayer ??= user.id;
    if (this.player?.id !== user.id) this.openedFor = null;
    this.player = user;
    this.token = token;
    this.open();
  }

  session(session: Session, event: AnalyticsEvent): void {
    this.identify(session.user, session.token);
    this.track(event);
  }

  forget(): void {
    this.player = null;
    this.token = null;
    this.openedFor = null;
  }

  retainToken(token: string | null): void {
    if (token !== this.token) this.forget();
  }

  track(name: AnalyticsEvent): void {
    try {
      if (this.player === null || this.token === null || !this.allowed(this.player.id)) return;
      const context = this.context();
      const acquisition =
        this.firstPlayer === this.player.id
          ? context
          : { ...context, landing_page: '', referrer: '' };
      const payload = { ...acquisition, event_id: crypto.randomUUID(), name };
      void this.send(payload, this.token).catch(() => {
        // Attribution is best effort; a failed analytics call cannot stop play.
      });
    } catch {
      // Unavailable browser metadata or permission must fail closed.
    }
  }

  private open(): void {
    try {
      if (
        this.player === null ||
        this.openedFor === this.player.id ||
        !this.allowed(this.player.id)
      )
        return;
      this.openedFor = this.player.id;
      this.track('client_opened');
    } catch {
      // Policy lookup failure never grants permission.
    }
  }
}

const CAMPAIGNS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function safeUrl(value: string, originOnly = false): string {
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    if (originOnly) return url.origin;
    const query = new URLSearchParams();
    for (const key of CAMPAIGNS) {
      const value = url.searchParams.get(key);
      if (value !== null) query.set(key, value.slice(0, 200));
    }
    // Lobby codes grant access to a table; attribution only needs the route.
    const path = url.pathname.replace(/^\/lobby\/.*$/i, '/lobby');
    return `${url.origin}${path}${query.size > 0 ? `?${query.toString()}` : ''}`;
  } catch {
    return '';
  }
}

/** Read only after permission. Navigation timing retains the original landing URL
 * even when client routing moved before device registration finished. No storage,
 * cookie, IP, user agent fingerprint, or pre-permission event queue is created. */
function browserContext(): Omit<AnalyticsPayload, 'event_id' | 'name'> {
  const landing = globalThis.performance?.getEntriesByType('navigation')[0]?.name;
  return {
    page: safeUrl(globalThis.location?.href ?? ''),
    landing_page: safeUrl(landing ?? globalThis.location?.href ?? ''),
    referrer: safeUrl(globalThis.document?.referrer ?? '', true),
  };
}
